<script lang="ts" setup>
import { ref, watch, computed, nextTick, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useQuasar } from 'quasar'
import { getYear } from 'date-fns'
import VueTurnstile from 'vue-turnstile'

import logger from './logger'
import logo from './assets/roadfootage-logo_603x75.png'
import icon from './assets/roadfootage-icon_512.png'
import { useSessionStore } from './stores/session/session.store'
import { useUserStore } from './stores/user/user.store'
import AccountRestrictedBanner from './components/AccountRestrictedBanner.vue'
import { useWalletStore } from './stores/wallet'
import { useNotificationsStore } from './stores/notifications'
import { usePushStore } from './stores/push'
import { useSignOut } from './composables/useSignOut'
import { storeToRefs } from 'pinia'
import { formatNumber } from './utils/format'
import { usePwaUpdate } from './composables/usePwaUpdate'
import { websiteLinks } from './config/site'
import IncidentSearchBox from './components/IncidentSearchBox.vue'
import NotificationBell from './components/notifications/NotificationBell.vue'
import { parseAuthError, AUTH_ERROR_CODES } from './constants/auth-errors'

const turnstileSiteId = import.meta.env.VITE_TURNSTILE_SITE_KEY

usePwaUpdate()

const router = useRouter()
const route = useRoute()
const $q = useQuasar()
const sessionStore = useSessionStore()
const userStore = useUserStore()
const walletStore = useWalletStore()
const { isAuthenticated, isLoggedIn, isLoading, isLoggingIn, user, role, authError } =
  storeToRefs(sessionStore)
const { checkSessionSilently } = sessionStore
const { pointsBalance } = storeToRefs(walletStore)
const { isRestricted } = storeToRefs(userStore)
// The wizard pages show the banner in place of their content, so skip the page-level one there.
const restrictedWizardRoutes = ['upload', 'submit-bounty', 'teslacam']
const showRestrictedBanner = computed(
  () => isRestricted.value && !restrictedWizardRoutes.includes(String(route.name))
)
const notificationsStore = useNotificationsStore()
const pushStore = usePushStore()

const now = ref(new Date())
const drawer = ref(false)
const initialized = ref(false)
const sessionLost = ref(false)

const showVerifyEmailDialog = ref(false)
const showAuthErrorDialog = ref(false)
const authErrorMessage = ref('')
let verifyEmailPollingInterval: ReturnType<typeof setInterval> | null = null

const VERIFY_EMAIL_POLL_INTERVAL_MS = 2_000
const VERIFY_EMAIL_MAX_ATTEMPTS = (15 * 60 * 1000) / VERIFY_EMAIL_POLL_INTERVAL_MS

const { signOut: doLogout } = useSignOut()

function doLogin() {
  return sessionStore.loginWithRedirect()
}

// Turnstile: show only when Auth0 has fully settled AND there is no authenticated session.
// isAuthenticated already incorporates hasValidAnonymousSession, so a valid persisted
// anonymous token suppresses the challenge without any extra condition here.
// Also suppress during auth error dialogs to avoid a confusing dual prompt.
const showTurnstile = computed(
  () =>
    !route.meta?.isPublic &&
    !isAuthenticated.value &&
    !isLoggingIn.value &&
    !showVerifyEmailDialog.value &&
    !showAuthErrorDialog.value
)

const turnstileToken = ref<string>('')
watch(turnstileToken, async (newToken) => {
  if (newToken) {
    logger.info('Received Turnstile token, exchanging for session...')
    await sessionStore.initialiseAnonymousSession(newToken)
    turnstileToken.value = ''
  }
})

async function initializeUserFeatures() {
  if (initialized.value) return
  initialized.value = true
  try {
    const isSetUp = await userStore.loadProfile()
    if (!isSetUp) {
      logger.info('User profile not set up. Redirecting to Get Started.')
      return router.push({ name: 'get-started' })
    }
    // After onboarding (which wins): a user on old terms is sent to accept them, and comes back to where they were.
    if (userStore.termsUpdateRequired) sendToUpdatedTerms()
    // Never prompts: it only refreshes a subscription this device already has.
    void pushStore.resync()
  } catch (e) {
    initialized.value = false // allow retry on next login
    logger.error(e, 'Failed to load user profile.')
    throw e
  }
}

// Stale terms can be found two ways: GET /me on sign-in (above) or a refused write (api middleware). Both end up here.
// They can still browse and reach /account, so this only moves them once, and not when they are already on the screen.
function sendToUpdatedTerms() {
  if (route.name === 'updated-terms' || route.name === 'get-started') return
  void router.push({ name: 'updated-terms', query: { redirect: route.fullPath } })
}
watch(
  () => userStore.termsUpdateRequired,
  (required, wasRequired) => {
    const onboarded = userStore.isProfileSetUp && userStore.isPreferencesSetUp
    if (required && !wasRequired && isLoggedIn.value && onboarded) sendToUpdatedTerms()
  }
)

// Notifications are polled only for a signed-in user with a profile. Anything else (sign out, session loss) stops the
// poll and clears the list so the next user never sees it.
const canReceiveNotifications = computed(() => isLoggedIn.value && userStore.isProfileSetUp)
watch(
  canReceiveNotifications,
  (canReceive) => {
    if (canReceive) notificationsStore.start()
    else notificationsStore.reset()
  },
  { immediate: true }
)

watch(isLoggedIn, (_isLoggedIn) => {
  if (_isLoggedIn) {
    stopVerifyEmailPolling()
    showVerifyEmailDialog.value = false
    // Reset so every fresh login triggers a profile load
    initialized.value = false
    nextTick(() => initializeUserFeatures())
  } else if (!isLoggingIn.value) {
    // Auth0 has fully settled and is not authenticated — genuine logout or session expiry.
    // Only redirect when the current route requires a logged-in session; routes accessible
    // to anonymous users (featured, explore, bounties, preview) can stay in place and the
    // Turnstile challenge will appear there if a fresh anonymous session is also needed.
    initialized.value = false
    if (route.meta.auth) {
      router.push({ name: 'explore' })
    }
  }
})

// Universal session recovery: when isAuthenticated drops then recovers (anonymous session
// re-established via Turnstile), replace the current route so the page re-mounts and
// retries all data fetches with the new token. Only fires after a genuine mid-session loss,
// not on the initial Turnstile challenge at page load.
watch(isAuthenticated, (now, prev) => {
  if (!isLoggingIn.value && prev && !now) {
    sessionLost.value = true
  } else if (now && sessionLost.value) {
    sessionLost.value = false
    router.replace(route.fullPath)
  }
})

// Close the drawer when navigating on smaller viewports
watch(
  () => route.name,
  () => {
    if ($q.screen.lt.md) drawer.value = false
  }
)

// Auth error handling: fires once Auth0 finishes initialising after a redirect callback
watch(isLoggingIn, (loading) => {
  if (loading || !authError.value?.message) return

  const { code, displayMessage } = parseAuthError(authError.value.message)
  router.replace({ path: route.path }) // clean error params from URL

  if (code === AUTH_ERROR_CODES.ERR_EMAIL_UNVERIFIED) {
    showVerifyEmailDialog.value = true
    startVerifyEmailPolling()
  } else {
    authErrorMessage.value = displayMessage
    showAuthErrorDialog.value = true
  }
})

function startVerifyEmailPolling() {
  let attempts = 0
  verifyEmailPollingInterval = setInterval(async () => {
    attempts++
    if (attempts >= VERIFY_EMAIL_MAX_ATTEMPTS || isLoggedIn.value) {
      stopVerifyEmailPolling()
      return
    }
    await checkSessionSilently()
    // isLoggedIn watcher closes the dialog and handles profile load on success
  }, VERIFY_EMAIL_POLL_INTERVAL_MS)
}

function stopVerifyEmailPolling() {
  if (verifyEmailPollingInterval != null) {
    clearInterval(verifyEmailPollingInterval)
    verifyEmailPollingInterval = null
  }
}

onUnmounted(() => stopVerifyEmailPolling())

function handleVerifyEmailAlreadyVerified() {
  stopVerifyEmailPolling()
  sessionStore.loginWithRedirect()
}

function handleVerifyEmailCancel() {
  stopVerifyEmailPolling()
  showVerifyEmailDialog.value = false
}

function handleAuthErrorDismiss() {
  showAuthErrorDialog.value = false
  router.push({ name: 'explore' })
}
</script>

<template>
  <q-layout view="hHh Lpr lff">
    <q-header elevated class="bg-grey-1 text-blue-grey-9">
      <q-toolbar>
        <q-btn dense flat round icon="mdi-menu" aria-label="Open menu" @click="drawer = !drawer" />

        <!-- Mobile: icon sits inline, left-aligned next to the hamburger -->
        <img
          v-if="!$q.screen.gt.sm"
          :src="icon"
          class="app-icon q-ml-xs"
          alt="Road Footage"
          draggable="false"
        />

        <!-- Push sign-in / chip to the far right -->
        <q-space />

        <!-- Desktop: inline box. Below that a magnifier opens /search, which has its own box. -->
        <incident-search-box v-if="$q.screen.gt.sm" class="header-search q-mr-md" />
        <q-btn
          v-else
          dense
          flat
          round
          icon="mdi-magnify"
          aria-label="Search incidents"
          :to="{ name: 'search' }"
        />

        <notification-bell v-if="canReceiveNotifications" />

        <q-btn v-show="!isLoggedIn" unelevated color="blue-8" @click="doLogin"> Sign in </q-btn>
        <q-chip v-show="isLoggedIn" icon="mdi-star-circle" color="blue-8" text-color="white" dense>
          {{ pointsBalance != null ? formatNumber(pointsBalance) : '—' }} pts
        </q-chip>
      </q-toolbar>

      <!-- Desktop: logo absolutely centred over the full header width.
           pointer-events: none so the hamburger and right-side controls stay clickable. -->
      <div v-if="$q.screen.gt.sm" class="toolbar-logo-centre">
        <img :src="logo" class="app-logo" alt="Road Footage logo" draggable="false" />
      </div>
    </q-header>

    <!--
      Drawer breakpoint 1023 = Quasar's md boundary.
      Below 1023px (phones + tablets): overlay mode with built-in backdrop scrim.
      Above 1023px (desktop): persistent sidebar that is part of the layout.
    -->
    <q-drawer v-model="drawer" show-if-above :width="280" :breakpoint="1023">
      <!-- Profile image header shown when logged in -->
      <q-img
        v-if="isLoggedIn"
        class="absolute-top rf-profile"
        src="https://cdn.quasar.dev/img/material.png"
        role="presentation"
      >
        <div class="absolute-bottom bg-transparent" v-if="user">
          <q-avatar size="56px" class="q-mb-sm">
            <img :src="user.picture" alt="" />
          </q-avatar>
          <div class="text-weight-bold">{{ user.name }}</div>
        </div>
      </q-img>

      <!--
        Column layout fills the full drawer height.
        When authenticated, offset by the profile image height (150px) so the
        scroll area starts below it — matching the profile image.
      -->
      <div class="column full-height" :style="isLoggedIn ? 'padding-top: 150px' : ''">
        <!-- Scrollable nav items -->
        <q-scroll-area class="col">
          <!-- Full logo shown at the top of the drawer on mobile when not logged in,
               since the header only shows the icon at that breakpoint -->
          <div v-if="$q.screen.lt.md && !isLoggedIn" class="row justify-center q-py-md">
            <img :src="logo" class="drawer-logo" alt="Road Footage" draggable="false" />
          </div>
          <!-- A nav, not a list: Quasar 2.35 gives link items no listitem role, so role="list" would have no items. -->
          <q-list role="navigation" aria-label="Main menu" padding>
            <q-item-label header>Browse</q-item-label>
            <q-item clickable v-ripple :to="{ name: 'explore' }">
              <q-item-section avatar>
                <q-icon name="mdi-map-marker" />
              </q-item-section>
              <q-item-section>Explore</q-item-section>
            </q-item>

            <q-item clickable v-ripple :to="{ name: 'featured' }">
              <q-item-section avatar>
                <q-icon name="mdi-star" />
              </q-item-section>
              <q-item-section>Featured</q-item-section>
            </q-item>

            <q-item clickable v-ripple :to="{ name: 'bounties' }">
              <q-item-section avatar>
                <q-icon name="mdi-bullhorn" />
              </q-item-section>
              <q-item-section>Bounties</q-item-section>
            </q-item>

            <div v-show="isLoggedIn && !userStore.isProfileSetUp">
              <q-item clickable v-ripple :to="{ name: 'get-started' }">
                <q-item-section avatar>
                  <q-icon name="mdi-star-circle" />
                </q-item-section>
                <q-item-section>Get Started</q-item-section>
              </q-item>
            </div>

            <q-separator spaced aria-hidden="true" />
            <q-item-label header>Share</q-item-label>

            <q-item
              v-if="!isRestricted"
              clickable
              v-ripple
              :to="isLoggedIn ? { name: 'upload' } : undefined"
              @click="!userStore.isProfileSetUp && doLogin()"
            >
              <q-item-section avatar>
                <q-icon name="mdi-video-wireless" />
              </q-item-section>
              <q-item-section>Share Footage</q-item-section>
            </q-item>

            <q-item
              v-if="!isRestricted"
              clickable
              v-ripple
              :to="isLoggedIn ? { name: 'submit-bounty' } : undefined"
              @click="!isLoggedIn && doLogin()"
            >
              <q-item-section avatar>
                <q-icon name="mdi-bullhorn-variant" />
              </q-item-section>
              <q-item-section>Post a Bounty</q-item-section>
            </q-item>

            <q-item
              v-if="!isRestricted"
              clickable
              v-ripple
              :to="isLoggedIn ? { name: 'teslacam' } : undefined"
              @click="!isLoggedIn && doLogin()"
            >
              <q-item-section avatar>
                <q-icon name="mdi-car-connected" />
              </q-item-section>
              <q-item-section>TeslaCam</q-item-section>
            </q-item>

            <div v-show="isLoggedIn && userStore.isProfileSetUp">
              <q-separator spaced aria-hidden="true" />
              <q-item-label header>Your Profile</q-item-label>

              <q-item clickable v-ripple :to="{ name: 'uploads' }">
                <q-item-section avatar>
                  <q-icon name="mdi-video" />
                </q-item-section>
                <q-item-section>Your Footage</q-item-section>
              </q-item>

              <q-item clickable v-ripple :to="{ name: 'my-bounties' }">
                <q-item-section avatar>
                  <q-icon name="mdi-bullhorn-outline" />
                </q-item-section>
                <q-item-section>Your Bounties</q-item-section>
              </q-item>

              <q-item clickable v-ripple :to="{ name: 'account' }">
                <q-item-section avatar>
                  <q-icon name="mdi-account" />
                </q-item-section>
                <q-item-section>Account</q-item-section>
              </q-item>
            </div>

            <div v-show="role === 'Staff'">
              <q-separator spaced aria-hidden="true" />
              <q-item-label header>Moderation</q-item-label>

              <q-item clickable v-ripple :to="{ name: 'moderation-dashboard' }">
                <q-item-section avatar>
                  <q-icon name="mdi-view-dashboard" />
                </q-item-section>
                <q-item-section>Dashboard</q-item-section>
              </q-item>

              <q-item clickable v-ripple :to="{ name: 'incident-reports' }">
                <q-item-section avatar>
                  <q-icon name="mdi-cog" />
                </q-item-section>
                <q-item-section>Incident Reports</q-item-section>
              </q-item>

              <q-item clickable v-ripple :to="{ name: 'moderation-bounties' }">
                <q-item-section avatar>
                  <q-icon name="mdi-bullhorn-outline" />
                </q-item-section>
                <q-item-section>Bounties</q-item-section>
              </q-item>

              <q-item clickable v-ripple :to="{ name: 'files' }">
                <q-item-section avatar>
                  <q-icon name="mdi-file" />
                </q-item-section>
                <q-item-section>Files</q-item-section>
              </q-item>

              <q-item clickable v-ripple :to="{ name: 'users' }">
                <q-item-section avatar>
                  <q-icon name="mdi-account-multiple" />
                </q-item-section>
                <q-item-section>Users</q-item-section>
              </q-item>
            </div>
          </q-list>
        </q-scroll-area>

        <!-- Footer: always visible at the bottom of the drawer, never scrolled away -->
        <div class="rf-drawer-footer">
          <q-separator />
          <q-list role="navigation" aria-label="Site links" dense>
            <q-item
              clickable
              v-ripple
              dense
              tag="a"
              :href="websiteLinks.about"
              target="_blank"
              rel="noopener"
            >
              <q-item-section>About</q-item-section>
            </q-item>
            <q-item
              clickable
              v-ripple
              dense
              tag="a"
              :href="websiteLinks.help"
              target="_blank"
              rel="noopener"
            >
              <q-item-section>Help</q-item-section>
            </q-item>
            <q-item
              clickable
              v-ripple
              dense
              tag="a"
              :href="websiteLinks.terms"
              target="_blank"
              rel="noopener"
            >
              <q-item-section>Terms of service</q-item-section>
            </q-item>
            <q-item
              clickable
              v-ripple
              dense
              tag="a"
              :href="websiteLinks.privacy"
              target="_blank"
              rel="noopener"
            >
              <q-item-section>Privacy policy</q-item-section>
            </q-item>
            <q-item
              clickable
              v-ripple
              dense
              tag="a"
              :href="websiteLinks.mediaLicence"
              target="_blank"
              rel="noopener"
            >
              <q-item-section>Media licence</q-item-section>
            </q-item>
            <q-item
              clickable
              v-ripple
              dense
              :to="{ name: 'report-a-problem', query: { from: route.path } }"
              data-testid="drawer-report-problem"
            >
              <q-item-section>Report a problem</q-item-section>
            </q-item>
            <q-item clickable v-ripple dense @click="doLogout" v-show="isLoggedIn">
              <q-item-section>Log out</q-item-section>
            </q-item>
          </q-list>
          <q-separator />
          <div class="text-caption text-grey-7 q-pa-sm">
            Copyright {{ getYear(now) }} Road Footage
          </div>
        </div>
      </div>
    </q-drawer>

    <q-dialog v-model="showVerifyEmailDialog" persistent>
      <q-card style="min-width: 320px; max-width: 480px">
        <q-card-section class="row items-center q-pb-none">
          <q-avatar icon="mdi-email-check-outline" color="primary" text-color="white" />
          <span class="q-ml-sm text-h6">Verify your email</span>
        </q-card-section>

        <q-card-section>
          <p>
            We sent a verification link to your email address. Please check your inbox and click the
            link to activate your account.
          </p>
          <p class="text-caption text-grey-7 q-mb-none">
            This page will update automatically once verified. If you've already verified on another
            device, press the button below.
          </p>
        </q-card-section>

        <q-card-actions class="row q-px-md q-pb-md">
          <q-btn flat label="Cancel" color="primary" @click="handleVerifyEmailCancel" />
          <q-space />
          <q-btn
            unelevated
            label="I've verified my email"
            color="primary"
            @click="handleVerifyEmailAlreadyVerified"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showAuthErrorDialog" persistent>
      <q-card style="min-width: 320px; max-width: 480px">
        <q-card-section class="row items-center q-pb-none">
          <q-avatar icon="mdi-alert-circle-outline" color="negative" text-color="white" />
          <span class="q-ml-sm text-h6">Sign in failed</span>
        </q-card-section>

        <q-card-section>
          <p>{{ authErrorMessage || 'Something went wrong during sign in. Please try again.' }}</p>
        </q-card-section>

        <q-card-actions align="right" class="q-px-md q-pb-md">
          <q-btn unelevated label="Continue" color="primary" @click="handleAuthErrorDismiss" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <transition name="fade" mode="out-in">
      <q-page-container>
        <AccountRestrictedBanner v-if="showRestrictedBanner" />
        <q-page v-if="showTurnstile">
          <div
            class="column justify-center items-center full-height full-width bg-grey-1"
            style="min-height: inherit"
          >
            <div class="text-center q-pa-md">
              <div class="text-h6 q-mb-md text-primary">Verifying your connection...</div>
              <vue-turnstile :site-key="turnstileSiteId" v-model="turnstileToken" theme="light" />
            </div>
          </div>
        </q-page>
        <router-view v-else-if="!isLoggingIn" />
        <q-inner-loading
          :showing="isLoading"
          label="Loading..."
          label-class="text-teal"
          label-style="font-size: 1.1em"
        />
      </q-page-container>
    </transition>
  </q-layout>
</template>

<style scoped>
.header-search {
  width: 240px;
}

.rf-profile {
  height: 150px;
}

.rf-drawer-footer {
  flex-shrink: 0;
  border-top: 1px solid #e0e0e0;
}

/* Desktop: overlay that spans the full header, centres the logo independently
   of the hamburger width on the left and the chip/button width on the right. */
.toolbar-logo-centre {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Full-width logo in the toolbar (desktop only) */
.app-logo {
  height: 2em;
  max-width: 45vw;
  width: auto;
  object-fit: contain;
  vertical-align: middle;
}

/* Icon in the toolbar (mobile) — natural proportions */
.app-icon {
  height: 1.5em;
  width: auto;
  object-fit: contain;
  vertical-align: middle;
}

/* Full logo displayed at the top of the drawer on mobile */
.drawer-logo {
  height: 2em;
  width: auto;
  max-width: 80%;
  object-fit: contain;
}
</style>
